import type { Discrepancy, TraceDocument } from "../../api/types";
import { S } from "../../styles";

/**
 * The pre-filled discrepancy list an inspector works from.
 *
 * The point of this screen is that it replaces a blank form. Someone at goods-in
 * used to open a box, read a certificate, and decide unaided whether it matched
 * an order they had no record of the terms of. Here the checks have already run
 * and each finding says what was expected, what arrived, and why that matters.
 *
 * Blocking findings first and in red, advisories after and muted — an inspector
 * scanning this needs to know within a second whether anything stops the part.
 *
 * A finding names the document it came from wherever one is to blame. Packs of
 * several certificates are the normal case, so "the certificate is for the wrong
 * part" is only actionable if the inspector can tell *which* of the six forms in
 * front of them it means. Findings about the pack as a whole — the quantity it
 * collectively covers, a contradiction between two forms — carry no document,
 * and are labelled as covering the whole pack rather than left unattributed.
 */

const TONE = {
  blocking: {
    bg: "#fff5f5",
    border: "#ffc9c9",
    fg: "#a61e1e",
    label: "Blocking",
  },
  advisory: {
    bg: "#fffaf0",
    border: "#ffe3bf",
    fg: "#8a5a1c",
    label: "Advisory",
  },
} as const;

export function DiscrepancyList({
  findings,
  documents,
}: {
  findings?: Discrepancy[] | null;
  documents?: TraceDocument[] | null;
}) {
  const items = findings ?? [];
  const filenameById = new Map(
    (documents ?? []).map((doc) => [doc.id, doc.filename]),
  );
  if (items.length === 0) {
    return (
      <p style={{ ...S.muted, fontSize: 13 }}>
        No discrepancies. The certificate matches what the order was placed on.
      </p>
    );
  }

  const blocking = items.filter((f) => f.severity === "blocking");
  const advisory = items.filter((f) => f.severity !== "blocking");

  return (
    <div>
      {[...blocking, ...advisory].map((f, i) => {
        const tone = TONE[f.severity === "blocking" ? "blocking" : "advisory"];
        return (
          <div
            key={`${f.code}-${i}`}
            role={f.severity === "blocking" ? "alert" : undefined}
            style={{
              background: tone.bg,
              border: `1px solid ${tone.border}`,
              borderRadius: 4,
              padding: "6px 8px",
              marginBottom: 6,
              fontSize: 12,
              color: tone.fg,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <strong>{f.message}</strong>
              <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>
                {tone.label}
              </span>
            </div>
            {(f.expected ?? f.actual) && (
              <div style={{ marginTop: 4, opacity: 0.85 }}>
                {f.expected && (
                  <span>
                    Expected: <code>{f.expected}</code>
                  </span>
                )}
                {f.expected && f.actual && <span> · </span>}
                {f.actual && (
                  <span>
                    On the document: <code>{f.actual}</code>
                  </span>
                )}
              </div>
            )}
            {/*
              The code is shown because it is what gets pushed to the ERP and
              quoted back in a supplier dispute — an inspector who has to refer
              to a finding needs the same identifier the ERP recorded.
            */}
            <div
              style={{
                marginTop: 2,
                opacity: 0.55,
                fontSize: 11,
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>{f.code}</span>
              <span style={{ fontStyle: "italic" }}>
                {f.document_id
                  ? (filenameById.get(f.document_id) ?? "one document")
                  : "across the pack"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
